import React, { useState, useEffect, useCallback } from 'react';
import type { ChangeEvent } from 'react';
import '../styles/navbar.css';
import { ActionButton, PrimaryButton } from '@fluentui/react';
import { NavLink, Outlet, useLocation, useNavigate, useRouteLoaderData } from 'react-router-dom';
import { apiFetch } from '../lib/api-client';
import { msalInstance } from '../lib/msal';
import type { Service } from '../interfaces/Service';
import { getSiteBranding, isPrGuardianPath } from './siteBranding';

type ServiceSummary = Pick<Service, 'id' | 'name'>;

type EnvironmentInfo = {
    isDatabaseExtractEnabled: boolean;
    environment: string;
};

type HamburgerButtonProps = {
    isOpen: boolean;
    onClick: () => void;
};

type NavbarLinkProps = {
    target: string;
    text: string;
    disabled?: boolean;
    onClick?: () => void;
};

type MoreButtonProps = {
    environmentInfo: EnvironmentInfo | null;
    selectedServiceName: string;
    isPrGuardian: boolean;
    prGuardianTarget: string | null;
};

type UserInfo = {
    isAuditor?: boolean;
};

// ===== Main Layout =====

export const PageLayout = () => {
    const [services, setServices] = useState<ServiceSummary[]>([]);
    const [selectedService, setSelectedService] = useState('');
    const [selectedServiceName, setSelectedServiceName] = useState('');
    const [environmentInfo, setEnvironmentInfo] = useState<EnvironmentInfo | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const userInfo = useRouteLoaderData('root') as UserInfo | null;
    const siteBranding = getSiteBranding(location.pathname);
    const isPrGuardian = isPrGuardianPath(location.pathname);
    const encodedServiceName = encodeURIComponent(selectedServiceName);
    const prGuardianTarget = selectedServiceName
        ? `/service/${encodedServiceName}/${userInfo?.isAuditor ? 'allcommits' : 'mycommits'}`
        : null;

    useEffect(() => {
        async function fetchServices() {
            try {
                const response = await apiFetch('/api/services');
                const data = await response.json() as ServiceSummary[];
                setServices(data);
                const storedServiceName = localStorage.getItem('selectedService');
                if (storedServiceName) {
                    const storedService = data.find((service) => service.name === storedServiceName);
                    if (storedService) {
                        setSelectedService(String(storedService.id));
                        setSelectedServiceName(storedServiceName);
                    }
                }
            } catch (error) {
                console.error('Error fetching services:', error);
            }
        }
        fetchServices();
    }, []);

    useEffect(() => {
        async function fetchEnvironmentInfo() {
            try {
                const response = await apiFetch('/api/environment');
                if (response.ok) {
                    const data = await response.json() as EnvironmentInfo;
                    setEnvironmentInfo(data);
                } else {
                    setEnvironmentInfo({ isDatabaseExtractEnabled: false, environment: 'unknown' });
                }
            } catch (error) {
                setEnvironmentInfo({ isDatabaseExtractEnabled: false, environment: 'unknown' });
            }
        }
        fetchEnvironmentInfo();
    }, []);

    const handleServiceChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
        const selectedId = event.target.value;
        const selectedServiceObj = services.find((service) => String(service.id) === selectedId);
        const name = selectedServiceObj?.name;
        if (name) {
            setSelectedService(selectedId);
            setSelectedServiceName(name);
            localStorage.setItem('selectedService', name);
            if (location.pathname !== '/') {
                navigate(`/service/${encodeURIComponent(name)}/allcommits`);
            }
            setMobileMenuOpen(false);
        }
    }, [services, navigate, location.pathname]);

    const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

    return (
        <>
            <nav className='navbar welcome-navbar'>
                <div className="no-padding-container">
                    <div className="navbar-left-group">
                        <a href='/' className='navbar-brand info'>
                            <img
                                src={siteBranding.icon}
                                height={46}
                                width={46}
                                className={location.pathname === '/' ? 'navbar-brand-icon is-home-icon' : 'navbar-brand-icon'}
                                alt=""
                                aria-hidden="true"
                            />
                            {siteBranding.name}
                        </a>
                        <select
                            className="service-dropdown"
                            value={selectedService}
                            onChange={handleServiceChange}
                            aria-label="Select a service"
                        >
                            <option value="" disabled>Select Service</option>
                            {services.map((service) => (
                                <option key={service.id} value={service.id}>{service.name}</option>
                            ))}
                        </select>
                    </div>

                    <HamburgerButton isOpen={mobileMenuOpen} onClick={() => setMobileMenuOpen(v => !v)} />

                    <div className={`navbar-buttons ${mobileMenuOpen ? 'is-open' : ''}`}>
                        {isPrGuardian && (
                            <>
                                <NavbarLink target={`/service/${encodedServiceName}/mycommits`} text="My commits" disabled={!selectedService} onClick={closeMobileMenu} />
                                <NavbarLink target={`/service/${encodedServiceName}/allcommits`} text="All commits" disabled={!selectedService} onClick={closeMobileMenu} />
                                <NavbarLink target="/faq" text="FAQ" onClick={closeMobileMenu} />
                            </>
                        )}
                        <MoreButton
                            environmentInfo={environmentInfo}
                            selectedServiceName={selectedServiceName}
                            isPrGuardian={isPrGuardian}
                            prGuardianTarget={prGuardianTarget}
                        />
                    </div>
                </div>

                {mobileMenuOpen && <div className="navbar-backdrop" onClick={closeMobileMenu} />}
            </nav>
            <Outlet context={{ selectedServiceName }} />
        </>
    );
};

// ===== Hamburger Button =====

function HamburgerButton({ isOpen, onClick }: HamburgerButtonProps) {
    return (
        <button
            className={`hamburger-btn ${isOpen ? 'is-open' : ''}`}
            onClick={onClick}
            aria-label="Toggle navigation"
            aria-expanded={isOpen}
        >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
        </button>
    );
}

// ===== Nav Link =====

function NavbarLink({ target, text, disabled = false, onClick }: NavbarLinkProps) {
    return (
        <ActionButton disabled={disabled}>
            <NavLink
                to={disabled ? '#' : target}
                className={({ isActive, isPending }) =>
                    isPending ? "navLinkActive" : isActive ? "navLinkActive" : "navLinkInactive"
                }
                onClick={onClick}
            >{text}</NavLink>
        </ActionButton>
    );
}

// ===== More Button =====

export const MoreButton = ({
    environmentInfo,
    selectedServiceName,
    isPrGuardian,
    prGuardianTarget,
}: MoreButtonProps) => {
    const navigate = useNavigate();
    const hasSelectedService = selectedServiceName.trim().length > 0;
    const encodedServiceName = encodeURIComponent(selectedServiceName);

    const baseMenuItems = [
        { key: 'signout', text: 'Sign Out', onClick: () => msalInstance.logoutRedirect() },
        ...(!isPrGuardian && prGuardianTarget
            ? [{ key: 'prguardian', text: 'PR Guardian', onClick: () => navigate(prGuardianTarget) }]
            : []),
        { key: 'stacktrace', text: 'Stack Trace Analyzer', onClick: () => navigate('/stacktrace') },
        { key: 'dailyapiprofiles', text: 'Daily api profiles and timelines', onClick: () => navigate('/dailyapiprofiles') },
        { key: 'adhocapiprofiler', text: 'Api profiler (adhoc)', onClick: () => navigate('/adhocapiprofiler') },
        ...(hasSelectedService
            ? [
                { key: 'profiles', text: 'Untrimmed profiles', onClick: () => navigate(`/service/${encodedServiceName}/profiles`) },
                { key: 'agenttasks', text: 'Agent task manager', onClick: () => navigate(`/service/${encodedServiceName}/agenttasks`) },
                { key: 'components', text: 'Components', onClick: () => navigate(`/service/${encodedServiceName}/components`) },
                ...(selectedServiceName.toUpperCase() === 'SPO'
                    ? [{ key: 'compute', text: 'SPO Compute Insights', onClick: () => navigate('/spocompute') }]
                    : []),
            ]
            : []),
        { key: 'backfill', text: 'BackFill', onClick: () => navigate('/backfill') }
    ];

    const menuItems = [...baseMenuItems];
    if (environmentInfo?.isDatabaseExtractEnabled === true) {
        menuItems.push({ key: 'mirror', text: 'Mirror', onClick: () => navigate('/dev/mirror') });
    }

    return <PrimaryButton className="more-button" text="More cool stuff..." persistMenu menuProps={{ shouldFocusOnMount: true, shouldFocusOnContainer: true, items: menuItems }} />;
};
